import React from 'react';
import type { FC } from 'react';
import { match } from 'variant';
import { useAsync, Spinner, Text } from '@wix/cli-ui-kit';
import { useDevCenterClient } from '@wix/dev-center-client';
import { CliError, CliErrorCode } from '@wix/cli-error';
import parse from 'semver/functions/parse.js';
import { useProjectModel } from '../../model.js';
import { assertBuildOutputExists } from '../../files/assert-build-output-exists.js';
import { Release } from './Release.js';

interface ReleaseCommandProps {
  baseUrl?: string;
  label?: string;
}

export const ReleaseCommand: FC<ReleaseCommandProps> = (props) => {
  const {
    model: {
      projectFolder,
      config: { appId },
    },
  } = useProjectModel();

  const { queryAppVersion } = useDevCenterClient();

  const { status } = useAsync(async () => {
    await assertBuildOutputExists(projectFolder);
    const { isLocked, isPublished, latestVersion } = await queryAppVersion({
      appId,
    });

    if (isLocked) {
      throw new CliError({
        code: CliErrorCode.CannotReleaseLockedApplication(),
        cause: undefined,
      });
    }

    const parsedLatestVersion = parse(latestVersion);
    if (!parsedLatestVersion) {
      throw new CliError({
        code: CliErrorCode.FailedToParseLatestVersion({
          version: latestVersion,
        }),
        cause: undefined,
        info: {
          latestVersion,
        },
      });
    }

    return { isPublished, latestVersion: parsedLatestVersion };
  }, []);

  return match(status, {
    Error: () => null,
    Loading: () => (
      <Spinner text={<Text>Loading application details...</Text>} />
    ),
    Success: ({ result }) => <Release {...props} {...result} />,
  });
};
